import { getLineItemNotReturnedQuantity } from "@babackapp/calculations/get-line-item-not-returned-quantity";
import { getLineItemRejectedByLogisticianQuantity } from "@babackapp/calculations/get-line-item-rejected-by-logistician-quantity";
import { getLineItemRejectedByMerchantQuantity } from "@babackapp/calculations/get-line-item-rejected-by-merchant-quantity";
import { getLineItemValidatedByMerchantQuantity } from "@babackapp/calculations/get-line-item-validated-by-merchant-quantity";
import { useInvalidateReturnRequest } from "@babackapp/private-api-client/client/use-invalidate-return-request";
import useUpdateLineItem from "@babackapp/private-api-client/client/use-update-line-item";
import type {
  ProductVariantDto,
  ReturnRequestLineItemDto,
  UpdateLineItemNewVariantDto,
} from "@babackapp/private-api-client/types";
import {
  newVariantRequestFromLineItem,
  newVariantRequestFromVariant,
} from "@/lib/exchange/new-variant-request";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@babackapp/ui/components/accordion";
import { Badge } from "@babackapp/ui/components/badge";
import { useIsRetractionOnly } from "@/hooks/use-is-retraction-only";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { ArticleItem } from "@/components/article-item/article-item";
import { useLineItemsListSections } from "../../../refund/hooks/use-line-items-list-sections";
import { useCurrentReturnRequest } from "../../../utils/current-return-request-context";
import { getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithWorkflowLogistician } from "../../../utils/get-line-item-ready-to-be-treated-by-merchant-selectable-quantity-with-workflow-logistician";
import { getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithoutWorkflowLogistician } from "../../../utils/get-line-item-ready-to-be-treated-by-merchant-selectable-quantity-without-workflow-logistician";
import { useSetLineItemIsLoading } from "../../../utils/line-items-loading-state-context";
import { getLineItemValidatedByLogisticianQuantity } from "@babackapp/calculations/get-line-item-validated-by-logistician-quantity";
import { getLineItemNotTreatedByLogisticianQuantity } from "@babackapp/calculations/get-line-item-not-treated-by-logistician-quantity";
import { useCanLogisticianRevalidateAfterMerchant } from "@/pages/(app)/returns/hooks/use-can-logistician-revalidate-after-merchant";
import useViewerRole from "@/lib/rights/use-viewer-role.ts";
import { getMerchantTreatedQuantityToDisplay } from "../../utils/get-merchant-treated-quantity-to-display";

export default function ReturnRequestArticles() {
  const { data: shop } = useCurrentShopSuspense();
  const isRetractionOnly = useIsRetractionOnly();

  const isLogistician = useViewerRole() === "logistician";

  const returnRequest = useCurrentReturnRequest();
  const invalidateReturnRequest = useInvalidateReturnRequest();
  const setLineItemLoading = useSetLineItemIsLoading();
  const t = useTranslation();

  const { mutateAsync: updateLineItem } = useUpdateLineItem({
    onSuccess: () => {
      invalidateReturnRequest({
        returnRequestId: returnRequest.id,
        shopId: shop.id,
      });
    },
  });

  const onUpdateResolution =
    (lineItemId: string) =>
    async (resolution: ReturnRequestLineItemDto["resolution"]) => {
      const lineItem = returnRequest.line_items.find(
        (item) => item.line_item_id === lineItemId,
      );

      if (!lineItem) {
        console.error("Line item not found");
        return;
      }

      /**
       * If we want an exchange, and no new_variant has been apoplied to
       * the line item, we create a new variant with the current variant
       *
       * Example: From Tshirt Black L -> Tshirt Black L by default
       *
       * So if the merchant wants to exchange the product and
       * does not select a new variant, shopify dont throw an
       * error because we are sending one by default
       */
      let newVariant: UpdateLineItemNewVariantDto | undefined;
      if (!lineItem.new_variant && resolution === "exchange") {
        newVariant = newVariantRequestFromLineItem(lineItem);
      }

      setLineItemLoading(lineItemId, true);
      updateLineItem({
        lineItemId,
        shopId: shop.id,
        returnRequestId: returnRequest.id,
        resolution,
        new_variant: newVariant,
      }).finally(() => setLineItemLoading(lineItemId, false));
    };

  const onUpdateVariant =
    (lineItemId: string) =>
    async (variant: ProductVariantDto, productTitle?: string | null) => {
      setLineItemLoading(lineItemId, true);
      await updateLineItem({
        lineItemId,
        shopId: shop.id,
        returnRequestId: returnRequest.id,
        new_variant: newVariantRequestFromVariant(variant, productTitle),
      }).finally(() => setLineItemLoading(lineItemId, false));
    };

  const {
    itemsNotReturned,
    itemsNotTreatedByLogistician,
    itemsValidatedByLogistician,
    itemsRejectedByLogistician,
    itemsRejectedByMerchant,
    itemsToShowToHandle,
    itemsValidatedByMerchant,
  } = useLineItemsListSections();

  const workflowLogisticianActivated = shop.workflowLogistician.activated;
  const canRevalidateAfterMerchant = useCanLogisticianRevalidateAfterMerchant();

  const isExchangeResolution = (lineItem: ReturnRequestLineItemDto) =>
    lineItem.resolution === "exchange";

  const notReturnedQuantity = itemsNotReturned.reduce(
    (acc, next) => acc + next.quantity_bought - next.quantity,
    0,
  );
  return (
    <div className="border-border border rounded-md flex-col gap-4 divide-y overflow-hidden">
      {itemsToShowToHandle.map((lineItem) => (
        <ArticleItem
          key={lineItem.line_item_id}
          lineItem={lineItem}
          showNewArticleCommandCode={isExchangeResolution(lineItem)}
          status="ready-for-merchant"
          onChangeResolution={
            isRetractionOnly
              ? undefined
              : onUpdateResolution(lineItem.line_item_id)
          }
          onChangeVariant={
            isRetractionOnly
              ? undefined
              : onUpdateVariant(lineItem.line_item_id)
          }
          quantity={
            workflowLogisticianActivated
              ? getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithWorkflowLogistician(
                  { lineItem },
                )
              : getLineItemReadyToBeTreatedByMerchantSelectableQuantityWithoutWorkflowLogistician(
                  { lineItem },
                )
          }
        />
      ))}
      {workflowLogisticianActivated &&
        itemsNotTreatedByLogistician.map((lineItem) => (
          <ArticleItem
            key={lineItem.line_item_id}
            lineItem={lineItem}
            showNewArticleCommandCode={isExchangeResolution(lineItem)}
            quantity={getLineItemNotTreatedByLogisticianQuantity({
              lineItem,
              allowRevalidationAfterMerchant: canRevalidateAfterMerchant,
            })}
            onChangeResolution={
              isRetractionOnly
                ? undefined
                : onUpdateResolution(lineItem.line_item_id)
            }
            onChangeVariant={
              isRetractionOnly
                ? undefined
                : onUpdateVariant(lineItem.line_item_id)
            }
            status="ready-for-logistician"
          />
        ))}
      {workflowLogisticianActivated &&
        itemsValidatedByLogistician.map((lineItem) => (
          <ArticleItem
            key={lineItem.line_item_id}
            lineItem={lineItem}
            onChangeResolution={
              isRetractionOnly
                ? undefined
                : onUpdateResolution(lineItem.line_item_id)
            }
            onChangeVariant={
              isRetractionOnly
                ? undefined
                : onUpdateVariant(lineItem.line_item_id)
            }
            quantity={getLineItemValidatedByLogisticianQuantity({
              lineItem,
            })}
            status="validated-by-logistician"
          />
        ))}
      {workflowLogisticianActivated &&
        itemsRejectedByLogistician.map((lineItem) => (
          <ArticleItem
            key={lineItem.line_item_id}
            lineItem={lineItem}
            onChangeResolution={
              isRetractionOnly
                ? undefined
                : onUpdateResolution(lineItem.line_item_id)
            }
            onChangeVariant={
              isRetractionOnly
                ? undefined
                : onUpdateVariant(lineItem.line_item_id)
            }
            quantity={getLineItemRejectedByLogisticianQuantity({
              lineItem,
            })}
            status="rejected-by-logistician"
          />
        ))}
      {isLogistician &&
        [...new Set([...itemsValidatedByMerchant, ...itemsRejectedByMerchant])]
          .map((lineItem) => ({
            lineItem,
            quantity: getMerchantTreatedQuantityToDisplay(
              lineItem,
              canRevalidateAfterMerchant,
            ),
          }))
          .filter(({ quantity }) => quantity > 0)
          .map(({ lineItem, quantity }) => {
            // An article the merchant only rejected must read as rejected
            // (red), not as a green "treated" article. Mixed validated +
            // rejected quantities keep the "treated" status for now — the full
            // per-status tag redesign is tracked separately.
            const isRejectedOnly =
              getLineItemRejectedByMerchantQuantity({ lineItem }) > 0 &&
              getLineItemValidatedByMerchantQuantity({ lineItem }) === 0;

            return (
              <ArticleItem
                key={lineItem.line_item_id}
                lineItem={lineItem}
                showNewArticleCommandCode={isExchangeResolution(lineItem)}
                quantity={quantity}
                status={
                  isRejectedOnly
                    ? "rejected-by-merchant"
                    : "treated-by-merchant"
                }
              />
            );
          })}
      {!isLogistician &&
        itemsValidatedByMerchant.map((lineItem) => (
          <ArticleItem
            key={lineItem.line_item_id}
            lineItem={lineItem}
            showNewArticleCommandCode={isExchangeResolution(lineItem)}
            quantity={getLineItemValidatedByMerchantQuantity({ lineItem })}
            status="treated-by-merchant"
          />
        ))}
      {!isLogistician &&
        itemsRejectedByMerchant.map((lineItem) => (
          <ArticleItem
            key={lineItem.line_item_id}
            lineItem={lineItem}
            quantity={getLineItemRejectedByMerchantQuantity({ lineItem })}
            status="rejected-by-merchant"
          />
        ))}
      {itemsNotReturned.length ? (
        <Accordion type="single" collapsible>
          <AccordionItem value="not-returned">
            <AccordionTrigger className="px-4">
              <span>
                {t("returnDetail.confirmModal.notReturned")}
                <Badge variant="secondary" className="ml-2">
                  {notReturnedQuantity} {t("orderPortal.sumup.articles")}
                </Badge>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-0 rounded-b-md overflow-hidden">
              <div className="flex-col gap-4 divide-y border-t">
                {itemsNotReturned.map((lineItem) => (
                  <ArticleItem
                    key={lineItem.line_item_id}
                    lineItem={lineItem}
                    showNewArticleCommandCode={isExchangeResolution(lineItem)}
                    quantity={getLineItemNotReturnedQuantity({ lineItem })}
                    status="not-returned"
                    hideReason
                    hideVariant
                    hideResolution
                    hideComment
                    hideImages
                    hideRejectReason
                  />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ) : null}
    </div>
  );
}
